import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button, ClevisMark, cn } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";

// The website chat (app.clevis.pro/chat/{slug}; ?embed=1 inside the bubble
// on the company's own site). It talks to the company's assistant, which
// books a visit into Requests for the office to approve — nothing is ever
// confirmed from here.

const APP = "https://app.clevis.pro";
const params = new URLSearchParams(window.location.search);
const EMBED = params.get("embed") === "1";
const PAGE = params.get("page") ?? "";

interface Info {
  enabled: boolean;
  company: string;
  assistantName: string;
  greeting: string;
  color: string;
  phone: string;
}
interface Turn {
  role: "user" | "assistant";
  content: string;
}

export function PublicChatPage({ slug }: { slug: string }) {
  const [info, setInfo] = useState<Info | null | "missing">(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    call<Info>("getChatInfo", { slug }, { mutation: false })
      .then((i) => setInfo(i))
      .catch(() => setInfo("missing"));
  }, [slug]);
  useEffect(() => endRef.current?.scrollIntoView({ block: "end" }), [turns, busy]);

  async function send(e: FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy || booked) return;
    const next = [...turns, { role: "user" as const, content: text }];
    setTurns(next);
    setDraft("");
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${APP}/api/chat/${encodeURIComponent(slug)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next, page: PAGE }),
      });
      const data = (await res.json()) as { ok: boolean; reply?: string; booked?: boolean; error?: string };
      if (!data.ok) throw new Error(data.error || "Something went wrong.");
      setTurns([...next, { role: "assistant", content: data.reply ?? "" }]);
      if (data.booked) setBooked(true);
    } catch (err) {
      setError((err as Error).message);
      setTurns(turns);
      setDraft(text);
    } finally {
      setBusy(false);
    }
  }

  const color = typeof info === "object" && info ? info.color : "#DF8048";
  const shell = cn("flex flex-col bg-white", EMBED ? "h-screen" : "mx-auto my-6 h-[600px] max-w-md rounded-2xl border border-steel/20 shadow-lg");
  if (info === null) return <div className={shell} />;
  if (info === "missing" || !info.enabled) {
    return (
      <div className={cn(shell, "items-center justify-center p-6 text-center text-sm text-steel")}>This chat isn't available right now.</div>
    );
  }
  return (
    <div className={shell}>
      <div className="flex items-center gap-2 rounded-t-2xl px-4 py-3 text-white" style={{ background: color }}>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{info.assistantName}</div>
          <div className="truncate text-[11px] opacity-90">{info.company}</div>
        </div>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3 text-sm">
        <Bubble side="left">{info.greeting}</Bubble>
        {turns.map((t, i) => (
          <Bubble key={i} side={t.role === "user" ? "right" : "left"} color={t.role === "user" ? color : undefined}>
            {t.content}
          </Bubble>
        ))}
        {busy ? <Bubble side="left">…</Bubble> : null}
        {booked ? (
          <p className="rounded-lg bg-success/10 px-3 py-2 text-xs text-success">
            Booked, pending confirmation from {info.company}.{" "}
            <button className="underline" onClick={() => { setTurns([]); setBooked(false); }}>
              Start another
            </button>
          </p>
        ) : null}
        {error ? <p className="text-xs text-danger">{error}{info.phone ? ` Or call ${info.phone}.` : ""}</p> : null}
        <div ref={endRef} />
      </div>
      <form className="flex items-end gap-2 border-t border-steel/15 p-2" onSubmit={send}>
        <textarea
          className="max-h-28 min-h-[40px] flex-1 resize-none rounded-lg border border-steel/30 px-2 py-1.5 text-sm text-ink focus:border-brandorange focus:outline-none"
          placeholder={booked ? "Thanks!" : "Type here…"}
          value={draft}
          disabled={booked}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
            }
          }}
        />
        <Button type="submit" busy={busy} disabled={booked} className="px-3 py-1.5 text-sm" style={{ background: color, borderColor: color }}>
          Send
        </Button>
      </form>
      <div className="flex items-center justify-center gap-1 pb-1.5 text-[10px] text-steel">
        <ClevisMark className="h-3 w-3" /> Powered by Clevis
      </div>
    </div>
  );
}

function Bubble({ side, color, children }: { side: "left" | "right"; color?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn("max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2", side === "right" ? "ml-auto text-white" : "bg-cream text-ink")}
      style={side === "right" && color ? { background: color } : undefined}
    >
      {children}
    </div>
  );
}
